import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import { Building2, CheckCircle2, ClipboardCheck, MailCheck, ShieldCheck } from "lucide-react";
import { FormEvent, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "wouter";

export function WholesaleLanding() {
  const processCards = [
    { Icon: ClipboardCheck, title: "Apply", text: "Tell us about your business and create your wholesale account." },
    { Icon: ShieldCheck, title: "Get approved", text: "We’ll review your application and notify you when your account is approved." },
    { Icon: Building2, title: "Order wholesale", text: "Sign in to access wholesale pricing and quickly restock your store." },
  ];
  const retailerBenefits = [
    "Wholesale pricing",
    "Easy online ordering",
    "Kratown powders, capsules & gummies",
    "KRYPT premium gummies",
    "Fast fulfillment",
    "Wholesale support",
  ];

  return (
    <main className="min-h-screen bg-[#07110f] text-white">
      <section className="container py-16 md:py-24">
        <p className="section-kicker">Kratown Trade</p>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl leading-[.92] md:text-7xl">
          Built for the shelves your customers return to.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-8 text-white/72">
          Apply for a Kratown wholesale account and get access to retailer pricing, easy online ordering, and products built for smoke and vape shops.
        </p>
        <div className="mt-9 flex flex-col gap-3 sm:flex-row">
          <Button asChild className="bg-[#c2ff45] text-[#07110f] hover:bg-[#d7ff82]">
            <Link href="/wholesale/apply">Apply for Wholesale</Link>
          </Button>
          <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10">
            <Link href="/wholesale/login">Wholesale login</Link>
          </Button>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-3">
          {processCards.map(({ Icon, title, text }) => (
            <article key={title} className="edge-panel-muted rounded-3xl p-6">
              <Icon className="h-5 w-5 text-[#c2ff45]" />
              <h2 className="mt-4 font-serif text-2xl uppercase">{title}</h2>
              <p className="mt-2 text-sm leading-6 text-white/65">{text}</p>
            </article>
          ))}
        </div>

        <section aria-labelledby="retailer-benefits-heading" className="mt-14 border-t border-white/10 pt-12 md:mt-16 md:pt-14">
          <div className="max-w-2xl">
            <p className="section-kicker">Built for retailers</p>
            <h2 id="retailer-benefits-heading" className="mt-4 font-serif text-4xl leading-[.95] md:text-5xl">
              A clear way to stock Kratown for your store.
            </h2>
          </div>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" aria-label="Wholesale program benefits">
            {retailerBenefits.map((benefit) => (
              <li key={benefit} className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[.035] px-4 py-4 text-sm leading-6 text-white/80">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-[#c2ff45]" aria-hidden="true" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
          <Button asChild className="mt-8 bg-[#c2ff45] text-[#07110f] hover:bg-[#d7ff82]">
            <Link href="/wholesale/apply">Apply for Wholesale</Link>
          </Button>
        </section>
      </section>
    </main>
  );
}

const WHOLESALE_BUSINESS_TYPES = [
  "Smoke / Vape Shop",
  "Convenience Store",
  "Specialty Retailer",
  "Distributor / Wholesaler",
  "Online Retailer",
  "Other",
] as const;

const US_STATES = [
  ["AL", "Alabama"], ["AK", "Alaska"], ["AZ", "Arizona"], ["AR", "Arkansas"], ["CA", "California"],
  ["CO", "Colorado"], ["CT", "Connecticut"], ["DE", "Delaware"], ["FL", "Florida"], ["GA", "Georgia"],
  ["HI", "Hawaii"], ["ID", "Idaho"], ["IL", "Illinois"], ["IN", "Indiana"], ["IA", "Iowa"],
  ["KS", "Kansas"], ["KY", "Kentucky"], ["LA", "Louisiana"], ["ME", "Maine"], ["MD", "Maryland"],
  ["MA", "Massachusetts"], ["MI", "Michigan"], ["MN", "Minnesota"], ["MS", "Mississippi"], ["MO", "Missouri"],
  ["MT", "Montana"], ["NE", "Nebraska"], ["NV", "Nevada"], ["NH", "New Hampshire"], ["NJ", "New Jersey"],
  ["NM", "New Mexico"], ["NY", "New York"], ["NC", "North Carolina"], ["ND", "North Dakota"], ["OH", "Ohio"],
  ["OK", "Oklahoma"], ["OR", "Oregon"], ["PA", "Pennsylvania"], ["RI", "Rhode Island"], ["SC", "South Carolina"],
  ["SD", "South Dakota"], ["TN", "Tennessee"], ["TX", "Texas"], ["UT", "Utah"], ["VT", "Vermont"],
  ["VA", "Virginia"], ["WA", "Washington"], ["WV", "West Virginia"], ["WI", "Wisconsin"], ["WY", "Wyoming"],
  ["DC", "District of Columbia"],
] as const;

export function WholesaleApply() {
  trpc.wholesale.csrf.useQuery();
  const apply = trpc.wholesale.submitApplication.useMutation();
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionLockRef = useRef(false);
  const textFields = [
    ["businessName", "Business name", "text", false],
    ["contactFirstName", "First name", "text", false],
    ["contactLastName", "Last name", "text", false],
    ["email", "Business email", "email", false],
    ["phone", "Phone", "tel", false],
    ["website", "Website (optional)", "url", true],
    ["resaleReference", "Resale certificate / seller's permit number (optional)", "text", true],
    ["address1", "Address", "text", false],
    ["address2", "Address line 2 (optional)", "text", true],
    ["city", "City", "text", false],
    ["postalCode", "Postal code", "text", false],
  ] as const;
  const fieldClassName = "border-white/15 bg-white/5 text-white";
  const selectClassName = "h-9 w-full rounded-md border border-white/15 bg-white/5 px-3 py-1 text-base text-white shadow-xs outline-none transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] md:text-sm";
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submissionLockRef.current || apply.isPending) return;

    submissionLockRef.current = true;
    setIsSubmitting(true);
    const data = new FormData(event.currentTarget);
    try {
      const result = await apply.mutateAsync(Object.fromEntries(data) as never);
      setMessage(result.created ? "Check your business email to verify your application, then our team can begin its review." : "An application already exists for this email address.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to submit application.");
    } finally {
      submissionLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#07110f] text-white">
      <section className="container max-w-4xl py-14">
        <Link href="/wholesale" className="text-sm text-[#c2ff45]">← Wholesale</Link>
        <h1 className="mt-5 font-serif text-5xl">Become a wholesale partner</h1>
        <p className="mt-4 max-w-2xl text-white/70">Business applications are reviewed before wholesale access or pricing is enabled.</p>
        <form onSubmit={onSubmit} className="edge-panel mt-8 grid gap-4 rounded-3xl p-6 md:grid-cols-2">
          {textFields.slice(0, 5).map(([name, label, type, optional]) => (
            <label key={name} className="grid gap-2 text-sm text-white/75">
              <span>{label}</span>
              <Input name={name} type={type} required={!optional} className={fieldClassName} />
            </label>
          ))}
          <label className="grid gap-2 text-sm text-white/75">
            <span>Password</span>
            <Input name="password" type="password" required minLength={12} className={fieldClassName} />
            <span className="text-xs text-white/50">Minimum 12 characters</span>
          </label>
          <label className="grid gap-2 text-sm text-white/75">
            <span>Business type</span>
            <select name="businessType" required defaultValue="" className={selectClassName}>
              <option value="" disabled className="bg-[#0d1916] text-white">Select business type</option>
              {WHOLESALE_BUSINESS_TYPES.map((businessType) => <option key={businessType} value={businessType} className="bg-[#0d1916] text-white">{businessType}</option>)}
            </select>
          </label>
          {textFields.slice(5, 10).map(([name, label, type, optional]) => (
            <label key={name} className="grid gap-2 text-sm text-white/75">
              <span>{label}</span>
              <Input name={name} type={type} required={!optional} className={fieldClassName} />
            </label>
          ))}
          <label className="grid gap-2 text-sm text-white/75">
            <span>State</span>
            <select name="state" required defaultValue="" className={selectClassName}>
              <option value="" disabled className="bg-[#0d1916] text-white">Select state</option>
              {US_STATES.map(([code, state]) => <option key={code} value={state} className="bg-[#0d1916] text-white">{state}</option>)}
            </select>
          </label>
          {textFields.slice(10).map(([name, label, type, optional]) => (
            <label key={name} className="grid gap-2 text-sm text-white/75">
              <span>{label}</span>
              <Input name={name} type={type} required={!optional} className={fieldClassName} />
            </label>
          ))}
          <div className="grid gap-4 pt-2 md:col-span-2">
            <p className="max-w-2xl text-sm leading-6 text-white/65">
              After submitting, check your email to verify your address. Your application will then be reviewed for wholesale access.
            </p>
            <div>
              <Button type="submit" disabled={isSubmitting || apply.isPending} className="bg-[#c2ff45] text-[#07110f] hover:bg-[#d7ff82]">
                {isSubmitting || apply.isPending ? "Submitting…" : "Submit Wholesale Application"}
              </Button>
            </div>
            {message ? <p role="status" className="text-sm text-white/75">{message}</p> : null}
          </div>
        </form>
      </section>
    </main>
  );
}

export function WholesaleLogin() {
  const [, setLocation] = useLocation();
  trpc.wholesale.csrf.useQuery();
  const login = trpc.wholesale.login.useMutation();
  const resendVerification = trpc.wholesale.resendEmailVerification.useMutation();
  const [message, setMessage] = useState<string | null>(null);
  const [verificationEmail, setVerificationEmail] = useState<string | null>(null);
  const onSubmit = async (event: FormEvent<HTMLFormElement>) => { event.preventDefault(); const data = new FormData(event.currentTarget); const email = String(data.get("email")).trim(); try { const result = await login.mutateAsync({ email, password: String(data.get("password")) }); if (result.status === "approved") { setLocation("/wholesale/shop"); return; } if (result.status === "verification_required") { setVerificationEmail(email); setMessage("Verify your business email before this application can be reviewed or activated."); return; } setMessage(result.status === "pending" ? "Your verified wholesale application is still pending review." : result.status === "revoked" ? "This wholesale account is no longer active. Please contact sales." : "No approved wholesale account was found for this login."); } catch (error) { setMessage(error instanceof Error ? error.message : "Unable to sign in."); } };
  const resend = async () => { if (!verificationEmail) return; await resendVerification.mutateAsync({ email: verificationEmail }); setMessage("If this application is awaiting verification, a new secure link has been sent to its business email."); };
  return <main className="min-h-screen bg-[#07110f] text-white"><section className="container max-w-xl py-20"><p className="section-kicker">Kratown Trade</p><h1 className="mt-4 font-serif text-5xl">Wholesale login</h1><form onSubmit={onSubmit} className="edge-panel mt-8 grid gap-4 rounded-3xl p-6"><Input name="email" type="email" placeholder="Business email" required className="border-white/15 bg-white/5 text-white"/><Input name="password" type="password" placeholder="Password" required className="border-white/15 bg-white/5 text-white"/><Button disabled={login.isPending} className="bg-[#c2ff45] text-[#07110f] hover:bg-[#d7ff82]">{login.isPending ? "Signing in…" : "Sign in"}</Button><div className="flex flex-wrap gap-x-5 gap-y-2 text-sm"><Link href="/wholesale/apply" className="text-[#c2ff45]">Apply for a wholesale account</Link><Link href="/wholesale/password-help" className="text-white/70 hover:text-[#c2ff45]">Forgot password?</Link></div>{verificationEmail ? <Button type="button" variant="outline" onClick={() => void resend()} disabled={resendVerification.isPending} className="border-white/20 text-white hover:bg-white/10">{resendVerification.isPending ? "Sending…" : "Resend verification email"}</Button> : null}{message ? <p role="status" className="text-sm text-white/75">{message}</p> : null}</form></section></main>;
}

export function WholesaleVerifyEmail() {
  const [, setLocation] = useLocation();
  const [token] = useState(() => new URLSearchParams(window.location.search).get("token") || "");
  const verify = trpc.wholesale.confirmEmailVerification.useMutation();
  const [result, setResult] = useState<"checking" | "verified" | "invalid">("checking");
  useEffect(() => {
    if (!token) { setResult("invalid"); return; }
    void verify.mutateAsync({ token }).then((response) => setResult(response.verified ? "verified" : "invalid")).catch(() => setResult("invalid"));
  }, [token]);
  return <main className="min-h-screen bg-[#07110f] text-white"><section className="container max-w-xl py-20"><p className="section-kicker">Kratown Trade</p><div className="edge-panel mt-6 rounded-3xl p-7 text-center"><MailCheck className="mx-auto h-8 w-8 text-[#c2ff45]"/>{result === "checking" ? <><h1 className="mt-5 font-serif text-4xl">Verifying your email</h1><p className="mt-3 text-white/70">Please wait while we secure your wholesale application.</p></> : result === "verified" ? <><CheckCircle2 className="mx-auto mt-5 h-7 w-7 text-[#c2ff45]"/><h1 className="mt-3 font-serif text-4xl">Email verified</h1><p className="mt-3 text-white/70">Your application can now be reviewed. We will email you when a decision is available.</p><Button onClick={() => setLocation("/wholesale/login")} className="mt-7 bg-[#c2ff45] text-[#07110f] hover:bg-[#d7ff82]">Go to wholesale login</Button></> : <><h1 className="mt-5 font-serif text-4xl">This link is no longer valid</h1><p className="mt-3 text-white/70">Return to wholesale login to sign in and request another verification email.</p><Button onClick={() => setLocation("/wholesale/login")} variant="outline" className="mt-7 border-white/20 text-white hover:bg-white/10">Back to login</Button></>}</div></section></main>;
}

export function WholesalePasswordHelp() {
  return <main className="min-h-screen bg-[#07110f] text-white"><section className="container max-w-xl py-20"><p className="section-kicker">Kratown Trade</p><div className="edge-panel mt-6 rounded-3xl p-7"><h1 className="font-serif text-4xl">Password assistance</h1><p className="mt-4 leading-7 text-white/70">Secure self-service password resets are not activated until the Medusa backend notification subscriber is configured. To protect your account, contact the Kratown wholesale team for assisted password support.</p><div className="mt-7 flex flex-wrap gap-3"><Button asChild className="bg-[#c2ff45] text-[#07110f] hover:bg-[#d7ff82]"><Link href="/contact">Contact wholesale</Link></Button><Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10"><Link href="/wholesale/login">Back to login</Link></Button></div></div></section></main>;
}

export function WholesaleDashboard() {
  const [, setLocation] = useLocation();
  trpc.wholesale.csrf.useQuery();
  const session = trpc.wholesale.session.useQuery(undefined, { retry: false });
  const logout = trpc.wholesale.logout.useMutation({ onSuccess: () => setLocation("/wholesale/login") });
  useEffect(() => { if (session.isError) setLocation("/wholesale/login"); }, [session.isError, setLocation]);
  if (session.isLoading) return <main className="min-h-screen bg-[#07110f] text-white"><section className="container py-20 text-white/65">Loading your wholesale account…</section></main>;
  if (!session.data) return null;
  const cards = [
    { title: "Wholesale shop", text: "Search and build a multi-SKU restock order with account-calculated pricing.", href: "/wholesale/shop", action: "Open Wholesale Shop" },
    { title: "Wholesale cart", text: "Review calculated trade totals before shipping, tax, and card checkout.", href: "/wholesale/cart", action: "Review cart" },
    { title: "Order history", text: "Review orders placed through this approved wholesale account and reorder eligible items.", href: "/wholesale/orders", action: "View wholesale orders" },
    { title: "Saved products", text: "Keep frequently ordered approved products ready for future trade orders.", href: "/wholesale/saved-products", action: "View saved products" },
    { title: "Account information", text: "Review the business and primary contact details associated with this account.", href: "/wholesale/account", action: "View account" },
    { title: "Contact sales", text: "Reach the Kratown wholesale team for account or order support.", href: "/contact", action: "Contact Kratown" },
  ];
  return <main className="min-h-screen bg-[#07110f] text-white"><section className="container py-14 md:py-20"><p className="section-kicker">Kratown Trade</p><div className="mt-4 flex flex-col justify-between gap-5 md:flex-row md:items-end"><div><h1 className="font-serif text-5xl">Welcome, {session.data.companyName}</h1><p className="mt-3 max-w-2xl text-white/70">Your approved wholesale account is active. Trade pricing and order tools are available only within this secure session.</p></div><Button variant="outline" onClick={() => logout.mutate()} disabled={logout.isPending} className="border-white/20 text-white hover:bg-white/10">{logout.isPending ? "Signing out…" : "Log out"}</Button></div><div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">{cards.map((card) => <article key={card.title} className="edge-panel-muted rounded-3xl p-6"><p className="text-sm uppercase tracking-[.16em] text-[#c2ff45]">{card.title}</p><p className="mt-3 text-sm leading-6 text-white/65">{card.text}</p><Link href={card.href} className="mt-5 inline-block text-sm text-[#d7ff82] underline decoration-[#c2ff45]/60 underline-offset-4">{card.action}</Link></article>)}<article className="rounded-3xl border border-dashed border-white/15 bg-white/[.02] p-6"><p className="text-sm uppercase tracking-[.16em] text-white/48">Catalog download</p><p className="mt-3 text-sm leading-6 text-white/52">A downloadable wholesale catalog will be enabled only after an active trade price schedule is published.</p><p className="mt-5 text-sm text-white/40">Not available yet</p></article></div></section></main>;
}
